/**
 * Category Management API Service
 */

import { apiClient } from './apiClient';
import { EventCategory, CategoryFormData, PaginatedResponse } from '../types/event';
import { logError } from '../utils/errorLogger';

// Removed CategoryListResponse since we get direct array from backend

export const categoryAPI = {
  // Get all categories (no pagination - they're simple and few)
  getCategories: async (): Promise<EventCategory[]> => {
    try {
      const response = await apiClient.get<EventCategory[] | PaginatedResponse<EventCategory>>(
        `/events/categories/`,
        { cache: true, ttl: 10 * 60 * 1000 } // Cache for 10 minutes
      );
      
      // Handle Django Rest Framework pagination response
      if (Array.isArray(response.data)) {
        // Direct array response (no pagination)
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated response with results array
        return response.data.results;
      } else {
        logError('Expected array or paginated response', { type: typeof response.data });
        return [];
      }
      
    } catch (error) {
      logError('Categories API error', error);
      throw error;
    }
  },

  // Get single category by ID
  getCategory: async (categoryId: number): Promise<EventCategory> => {
    const response = await apiClient.get<EventCategory>(
      `/events/categories/${categoryId}/`,
      { cache: true, ttl: 10 * 60 * 1000 }
    );
    return response.data;
  },

  // Create new category (Admin only)
  createCategory: async (categoryData: CategoryFormData): Promise<EventCategory> => {
    const response = await apiClient.post<EventCategory>('/events/categories/', categoryData);

    // Invalidate categories cache after creating
    await apiClient.invalidateCache('/events/categories/');
    
    return response.data;
  },

  // Update existing category (Admin only)
  updateCategory: async (categoryId: number, categoryData: Partial<CategoryFormData>): Promise<EventCategory> => {
    const response = await apiClient.patch<EventCategory>(
      `/events/categories/${categoryId}/`,
      categoryData
    );

    // Invalidate categories cache after updating
    await apiClient.invalidateCache('/events/categories/');
    
    return response.data;
  },

  // Delete category (Admin only)
  deleteCategory: async (categoryId: number): Promise<void> => {
    await apiClient.delete(`/events/categories/${categoryId}/`);

    // Invalidate categories cache after deleting
    await apiClient.invalidateCache('/events/categories/');
  }
};

export default categoryAPI;
